import { FC, useCallback, useEffect, useMemo, useRef, useState } from "react"
import Autocomplete, { Option } from "../form/autocomplete"
import { useSearchLoanOfficerQuery } from "../../../redux/queries"
import { LoanOfficer, LoSearchParams } from "../../../types"
import { checkIsAdmin } from "../../utils"
import RoundedImg from "../rounded-img"

type SearchMode = "name" | "nmls"

const INITIALS_COLORS = [
  "bg-primary-400",
  "bg-amber-500",
  "bg-emerald-500",
  "bg-rose-500",
  "bg-violet-500",
  "bg-sky-500",
  "bg-orange-500",
  "bg-teal-500"
]

const getInitialsColor = (name: string) => {
  let hash = 0
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash)
  }
  return INITIALS_COLORS[Math.abs(hash) % INITIALS_COLORS.length]
}

const getInitials = (name: string) =>
  name
    .split(" ")
    .filter(Boolean)
    .map(p => p[0])
    .slice(0, 2)
    .join("")
    .toUpperCase()

interface Props {
  searchParams?: LoSearchParams
  selected?: string
  label?: string
  direction?: "vertical" | "horizontal"
  onlyAdmins?: boolean
  required?: boolean
  excludeIds?: string[]
  showBrandName?: boolean
  showSearchToggle?: boolean
  onChange: (val: string) => void
  onSelectLo?: (lo: LoanOfficer) => void
}

const convertLoToAutocompleteOption = (
  lo: LoanOfficer,
  showBrandName = false
) => ({
  id: lo.uid,
  text:
    showBrandName && lo.brand_name ? `${lo.name} (${lo.brand_name})` : lo.name
})

const LoanOfficerAutocomplete: FC<Props> = ({
  searchParams = {},
  label = "Loan officer:",
  selected,
  direction = "horizontal",
  onlyAdmins,
  required = false,
  excludeIds = [],
  showBrandName = false,
  showSearchToggle = false,
  onChange,
  onSelectLo
}) => {
  const [query, setQuery] = useState<string>("")
  const [debouncedQuery, setDebouncedQuery] = useState<string>("")
  const [searchMode, setSearchMode] = useState<SearchMode>("name")
  // Store LO data in a map so we can find it when selected (even after search results change)
  const loMapRef = useRef<Map<string, LoanOfficer>>(new Map())

  // Debounce query to reduce API calls
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setDebouncedQuery(query)
    }, 300) // 300ms debounce delay

    return () => clearTimeout(timeoutId)
  }, [query])

  const searchQueryParams = useMemo(() => {
    const params: Record<string, string | number | boolean> = {
      limit: "10",
      ...searchParams
    }
    if (searchMode === "name") {
      params.fullname = debouncedQuery
    } else {
      params.nmls = debouncedQuery
    }
    return params
  }, [searchMode, debouncedQuery, searchParams])

  const { data, isFetching } = useSearchLoanOfficerQuery(searchQueryParams)

  // Update the LO map whenever we get new search results
  useEffect(() => {
    if (data) {
      data.forEach(lo => {
        loMapRef.current.set(lo.uid, lo)
      })
    }
  }, [data])

  const filteredData = useMemo(() => {
    let filtered = data ?? []

    if (onlyAdmins) {
      filtered = filtered.filter(lo => checkIsAdmin(lo.roles ?? []))
    }

    if (excludeIds.length > 0) {
      filtered = filtered.filter(lo => !excludeIds.includes(lo.uid))
    }

    return filtered
  }, [data, onlyAdmins, excludeIds])

  const options = useMemo(
    () =>
      filteredData.map(lo => convertLoToAutocompleteOption(lo, showBrandName)),
    [filteredData, showBrandName]
  )

  const selectedOption = options.find(opt => opt.id === selected)

  const { data: selectedLo, isFetching: isFetchingSelectedLo } =
    useSearchLoanOfficerQuery(
      {
        loid: selected ?? ""
      },
      { skip: !selected || !!selectedOption }
    )

  // Also add selected LO to the map
  useEffect(() => {
    if (selectedLo?.[0]) {
      loMapRef.current.set(selectedLo[0].uid, selectedLo[0])
    }
  }, [selectedLo])

  const handleChange = (option: { id: string; text: string }) => {
    onChange(option.id)
    if (onSelectLo) {
      // Look up in the map first, then fall back to current data
      const lo =
        loMapRef.current.get(option.id) ??
        filteredData.find(l => l.uid === option.id) ??
        selectedLo?.[0]
      if (lo) {
        onSelectLo(lo)
      }
    }
  }

  const handleSearchModeChange = (mode: SearchMode) => {
    setSearchMode(mode)
    setQuery("")
    setDebouncedQuery("")
  }

  const renderLoOption = useCallback(
    (option: Option, { active }: { selected: boolean; active: boolean }) => {
      const lo = loMapRef.current.get(option.id)
      const subtitle = lo?.title || lo?.position
      const picture = lo?.picture

      return (
        <div className="flex items-center gap-3">
          {picture ? (
            <RoundedImg
              size={10}
              className="flex-shrink-0"
              src={picture}
              alt={option.text}
            />
          ) : (
            <div
              className={`flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center text-sm font-medium text-white ${getInitialsColor(
                option.text
              )}`}
            >
              {getInitials(option.text)}
            </div>
          )}
          <div className="min-w-0">
            <p
              className={`font-medium truncate ${
                active ? "text-white" : "text-gray-900"
              }`}
            >
              {option.text}
            </p>
            {subtitle && (
              <p
                className={`text-sm truncate ${
                  active ? "text-white/70" : "text-neutral-500"
                }`}
              >
                {subtitle}
              </p>
            )}
          </div>
        </div>
      )
    },
    []
  )

  const loFooter = (
    <div className="border-t border-neutral-100 bg-neutral-50 px-4 py-3 text-xs text-neutral-500">
      Can&apos;t find your loan officer? Type their name directly in the field.
    </div>
  )

  return (
    <div className="max-w-md">
      {showSearchToggle && (
        <div className="flex gap-1 mb-2">
          <button
            type="button"
            onClick={() => handleSearchModeChange("name")}
            className={`px-3 py-1 text-xs font-medium rounded-md transition-colors ${
              searchMode === "name"
                ? "bg-primary-100 text-primary-700"
                : "bg-gray-100 text-gray-600 hover:bg-gray-200"
            }`}
          >
            Name
          </button>
          <button
            type="button"
            onClick={() => handleSearchModeChange("nmls")}
            className={`px-3 py-1 text-xs font-medium rounded-md transition-colors ${
              searchMode === "nmls"
                ? "bg-primary-100 text-primary-700"
                : "bg-gray-100 text-gray-600 hover:bg-gray-200"
            }`}
          >
            NMLS
          </button>
        </div>
      )}
      <Autocomplete
        label={label}
        name="loan_officer"
        placeholder={
          searchMode === "name" ? "Search names..." : "Search by NMLS..."
        }
        value={
          selectedOption ??
          (selectedLo?.[0]
            ? convertLoToAutocompleteOption(selectedLo?.[0], showBrandName)
            : undefined)
        }
        query={query}
        options={options}
        direction={direction}
        required={required}
        renderOption={renderLoOption}
        footer={loFooter}
        onChange={handleChange}
        onChangeQuery={setQuery}
        isLoading={isFetching || isFetchingSelectedLo}
      />
    </div>
  )
}

export default LoanOfficerAutocomplete
